import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Title } from "../../themes/Default/components/Title";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button } from "../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../themes/Default/components/List";
import { Header as ListHeader } from "../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../themes/Default/components/List/components/Body";
import { Grid as ListGrid } from "../../themes/Default/components/List/components/Grid";
import { Card } from "../../themes/Default/components/List/components/Grid/components/Card";
import { Pagination } from "../../themes/Default/components/List/components/Footer/components/Pagination";
import { TableHead } from "../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Title as ListTitle } from "../../themes/Default/components/List/components/Header/components/Title";
import { Menu as ListMenu } from "../../themes/Default/components/List/components/Header/components/Menu";
import { Filter as ListFilter } from "../../themes/Default/components/List/components/Header/components/Filter";
import { Button as ListMenuButton } from "../../themes/Default/components/List/components/Header/components/Menu/components/Button";
import { Checkbox } from "../../themes/Default/components/Form/components/Checkbox";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { employeesManagement } from "../../hooks/api/EmployeesManagement";
import { getNumberOfUsedFilters } from "../../services/utils/Filter";
import { useTranslation } from "react-i18next";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { employeeRolesManagement } from "../../hooks/api/EmployeeRolesManagement/EmployeeRolesManagement";
import { prepareSelect } from "../../services/core/Select";
import { Toaster } from "../../themes/Default/components/Toaster";
import approvedIcon from "../../assets/icons/approved.svg";
import errorIcon from "../../assets/icons/error.svg";
import { isGridView } from "../../services/core/User/IsGridView";
import { useUserToken } from "../../hooks/core/UserToken";
import { usePersistentFilters } from "../../hooks/core/PersistentFilters";

const Employees = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 50;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
        firstName: "",
        lastName: "",
        role: "",
    };
    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [filters, setFilters, resetFilters] = usePersistentFilters("employeesFilters", defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [numberOfUserdFilters, setNumberOfUserdFilters] = useState();
    const [totalRecords, setTotalRecords] = useState(0);
    const [employees, setEmployees] = useState();
    const [roleOptions, setRoleOptions] = useState([]);
    const { useEmployeesListMutation, useDeleteEmployeeMutation, useViewAvatarMutation, useExportEmployeesMutation } = employeesManagement;
    const [employeesList, { isLoading }] = useEmployeesListMutation();
    const [deleteEmployee] = useDeleteEmployeeMutation();
    const [exportEmployees] = useExportEmployeesMutation();
    const { useRoleListMutation } = employeeRolesManagement;
    const [roleList] = useRoleListMutation();
    const [isGridActive, setIsGridActive] = useState(isGridView(decodedToken, 'employees'));
    const [viewAvatar] = useViewAvatarMutation();
    const [avatars, setAvatars] = useState({});

    const openFilter = () => {
        setShowFilter(true);
    };

    const closeFilter = () => {
        setShowFilter(false);
    };

    const gotoNew = () => {
        navigate("/employees/add");
    };

    const gotoEdit = (employeeId) => {
        navigate("/employees/edit/" + employeeId);
    };

    const handleGridView = () => {
        setIsGridActive(!isGridActive);
    };

    const onFilter = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onFiltered = (values) => {
        setFilters({
            ...filters,
            firstName: values.firstName,
            lastName: values.lastName,
            role: values.role,
            isArchived: values.isArchived,
        });
    };

    const onPageChange = (currentPage, startPage, limitPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedEmployees((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDeleteOne = (id, firstName, lastName) => {
        toast.warn(
            ({ closeToast }) => (
                <div className="notification">
                    <div className="notification-message">
                        <p className="notification-message__title">
                            {firstName} {lastName}
                        </p>
                        <p className="notification-message__description">{t("areYouSure")}</p>
                    </div>
                    <div className="notification-options">
                        <div
                            className="notification-options__no"
                            onClick={() => {
                                closeToast();
                            }}
                        >
                            <img src={errorIcon}></img>
                            <span className="span-no">{t("no")}</span>
                        </div>
                        <div
                            className="notification-options__yes"
                            onClick={() => {
                                closeToast();
                                const employeeId = id;
                                deleteEmployee(employeeId).then((result) => {
                                    if (result?.error) {
                                        toast.error(result.error.data.error.responseMessage);
                                    } else {
                                        employeesList(filters).then((result) => {
                                            const employees = result?.data?.records;
                                            setEmployees(employees);
                                            setTotalRecords(result?.data?.totalRecords);
                                        });
                                        setDisableDelete(true);
                                        toast.info(result?.data?.info?.responseMessage);
                                        onSelectionChange(employeeId);
                                    }
                                });
                            }}
                        >
                            <img src={approvedIcon}></img>
                            <span className="span-yes">{t("yes")}</span>
                        </div>
                    </div>
                </div>
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedEmployees?.length; i++) {
                            const employeeId = selectedEmployees[i];
                            deleteEmployee(employeeId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    employeesList(filters).then((result) => {
                                        const employees = result?.data?.records;
                                        setEmployees(employees);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(employeeId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onExport = async () => {
        const blob = await exportEmployees().unwrap();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date().toISOString().split('T')[0];
        link.download = `Employees_${today}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const getInitials = (firstName, lastName) => {
        let initials = firstName.charAt(0) + lastName.charAt(0);

        return initials;
    };

    const avatarUrl = (employeeId) => {
        viewAvatar(employeeId).then((result) => {
            if (result?.data?.avatar) {
                setAvatars((prevAvatars) => {
                    const updatedAvatars = { ...prevAvatars, [employeeId]: result?.data?.avatar };
                    return updatedAvatars;
                });
            }
        });
    };

    useEffect(() => {
        if (selectedEmployees.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedEmployees]);

    useEffect(() => {
        employeesList(filters).then((result) => {
            const employees = result?.data?.records;
            setEmployees(employees);
            setTotalRecords(result?.data?.totalRecords);

            if (employees && Array.isArray(employees)) {
                employees.forEach((employee) => {
                    avatarUrl(employee.id);
                });
            }
        });
        setNumberOfUserdFilters(getNumberOfUsedFilters(filters));
    }, [filters, employeesList]);

    useEffect(() => {
        roleList().then((result) => {
            let options = [];
            result?.data?.records.forEach((role) => {
                options.push({
                    id: role.uniqueKey,
                    name: role.name,
                });
            });
            setRoleOptions(prepareSelect(options));
        });
    }, [roleList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <Button title={t("employees.addEmployee")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
                <Button title={t("export")} icon="fa-regular fa-cloud-arrow-down" onClick={onExport}></Button>
            </TopNavigation>
            <Main>
                <Title>{t("employees.title")}</Title>
                <Breadcrumps>
                    <Link title={t("employees.title")} icon="fa-user-tie" />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List isGridActive={isGridActive}>
                            <ListHeader>
                                <ListTitle title={`${t("employees.title")} (${totalRecords})`} subtitle="Equilibrio Kids and Teens" />
                                <ListMenu>
                                    <ListMenuButton
                                        title={isGridActive ? "List view" : "Card view"}
                                        icon={isGridActive ? "fa-list-ul" : "fa-grid-2"}
                                        onClick={handleGridView}
                                    />
                                    <ListMenuButton title="Filter" icon="fa-filter" onClick={openFilter} badge={numberOfUserdFilters} />
                                </ListMenu>
                                <ListFilter isVisible={showFilter} onClose={closeFilter} onFilter={onFilter} onResetFilter={resetFilters}>
                                    <Formik
                                        innerRef={formRef}
                                        initialValues={{ firstName: filters?.firstName, lastName: filters?.lastName, role: filters?.role }}
                                        onSubmit={onFiltered}
                                    >
                                        {({ values, handleChange, handleSubmit, resetForm, setFieldValue }) => (
                                            <Form>
                                                <Row>
                                                    <Field>
                                                        <Input
                                                            name="firstName"
                                                            label={t("employees.firstName")}
                                                            value={values.firstName}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Input
                                                            name="lastName"
                                                            label={t("employees.lastName")}
                                                            value={values.lastName}
                                                            onChange={handleChange}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Select
                                                            name="role"
                                                            label={t("employees.role")}
                                                            options={roleOptions}
                                                            onChange={handleChange}
                                                            selectedValue={values.role}
                                                        />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Checkbox
                                                            name="isArchived"
                                                            label={t("groups.isArchived")}
                                                            checked={values.isArchived}
                                                            onChange={(e) => setFieldValue("isArchived", e.target.checked)}
                                                        />
                                                    </Field>
                                                </Row>
                                            </Form>
                                        )}
                                    </Formik>
                                </ListFilter>
                            </ListHeader>
                            {isGridActive ? (
                                <ListGrid>
                                    {employees?.length > 0 &&
                                        employees?.map((employee) => {
                                            return (
                                                <Card
                                                    key={employee.id}
                                                    firstName={employee.firstName}
                                                    lastName={employee.lastName}
                                                    roleName={employee.roleName}
                                                    avatar={avatars[employee.id]}
                                                    addressStreet={employee.addressStreet}
                                                    addressNumber={employee.addressNumber}
                                                    onEditClick={() => gotoEdit(employee.id)}
                                                    onDeleteClick={() => onDeleteOne(employee.id, employee.firstName, employee.lastName)}
                                                    initials={getInitials(employee.firstName, employee.lastName)}
                                                />
                                            );
                                        })}
                                </ListGrid>
                            ) : (
                                <ListBody>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeadCell></TableHeadCell>
                                            <TableHeadCell></TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("employees.firstName")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("employees.lastName")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("employees.phoneNumber")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("employees.phoneNumberPrivate")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("employees.email")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell>
                                                <span>{t("employees.role")}</span>
                                            </TableHeadCell>
                                            <TableHeadCell></TableHeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {employees?.length > 0 &&
                                            employees?.map((employee) => {
                                                return (
                                                    <TableRow key={employee.id}>
                                                        <TableDataCell>
                                                            <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(employee.id)}></Button>
                                                        </TableDataCell>
                                                        <TableDataCell>
                                                            <Checkbox name="isBlockClass" value={employee.id} onChange={() => onSelectionChange(employee.id)} />
                                                        </TableDataCell>
                                                        <TableDataCell>{employee.firstName}</TableDataCell>
                                                        <TableDataCell>{employee.lastName}</TableDataCell>
                                                        <TableDataCell>{employee.phoneNumber}</TableDataCell>
                                                        <TableDataCell>{employee.phoneNumberPrivate}</TableDataCell>
                                                        <TableDataCell>{employee.emailAddress}</TableDataCell>
                                                        <TableDataCell>{employee.roleName}</TableDataCell>
                                                        <TableDataCell></TableDataCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </ListBody>
                            )}
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Employees;
